import { formatIp, formatMoney, formatTime, parseMysqlTime } from '@/lib/format';
import type { BetlensResult } from '@/types';
import { snapshotFilename } from './snapshot';

/**
 * A self-contained HTML report of one diagnostic — the same snapshot, rendered
 * for a human instead of a machine. Everything is inline (no external requests),
 * so it opens anywhere and prints to a clean PDF. The full raw bundle is
 * appended in a collapsed appendix, so nothing is lost.
 */

const esc = (v: unknown): string =>
  String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const SEVERITY_COLOR: Record<string, string> = {
  critical: '#b3261e',
  warning: '#8a6100',
  info: '#0a5c9e',
};

const STATUS_COLOR: Record<string, string> = {
  ok: '#1a7f37',
  empty: '#6e7b8b',
  skipped: '#aab2bd',
  unavailable: '#b3261e',
};

export function buildReportHtml(result: BetlensResult, opts: { autoPrint?: boolean } = {}): string {
  const { bundle, hints } = result;
  const r = bundle.resolved;
  const meta = bundle.currencyMetadata.data;
  const trx = bundle.transactions.data ?? [];
  const bet = trx.find((t) => t.action === 'bet');
  const wins = trx.filter((t) => t.action === 'win');
  const won = wins.reduce((s, w) => s + Number(w.amount ?? 0), 0);
  const takenAt = new Date().toISOString().replace('T', ' ').slice(0, 19);

  const money = (a: unknown, c?: string | null) => esc(formatMoney(a as string, meta, c));

  const metaRows = [
    ['Bet ID', esc(r.bet_id)],
    ['Game', `${esc(r.game?.name ?? '—')} <span class="dim">${esc(r.game?.type ?? '')}</span>`],
    ['Operator (play_service_id)', esc(r.play_service_id)],
    ['Player', esc(trx.find((t) => t.player_id)?.player_id ?? '—')],
    ['Cycle', `<code>${esc(r.cycle_uniq_id)}</code>`],
    ['Stake → payout', `${money(bet?.amount, bet?.currency)} → ${wins.length ? money(won, wins[0]?.currency) : '—'}`],
  ]
    .map(([k, v]) => `<tr><th>${k}</th><td>${v}</td></tr>`)
    .join('');

  const hintsHtml = hints.length
    ? hints
        .map(
          (h) => `<div class="hint" style="border-left-color:${SEVERITY_COLOR[h.severity] ?? '#888'}">
            <div class="hint-h"><span class="sev" style="color:${SEVERITY_COLOR[h.severity] ?? '#888'}">${esc(h.severity)}</span> ${esc(h.name)} <code class="dim">${esc(h.ruleId)}</code></div>
            <div>${esc(h.error ?? h.message)}</div>
          </div>`,
        )
        .join('')
    : '<p class="dim">No hints matched — nothing known is wrong with this bet.</p>';

  const SOURCES: [string, keyof typeof bundle][] = [
    ['primary db', 'transactions'], ['game bet', 'bet'], ['round', 'roundDetails'],
    ['realtime', 'clickhouseRealtime'], ['attempt log', 'clickhouseMv'], ['settled', 'clickhouseSync'],
    ['retries', 'delayDelivery'], ['sessions', 'sessions'], ['freebets', 'freebets'],
    ['rtp', 'rtpVariation'], ['currency', 'currencyMetadata'], ['logs', 'logs'], ['traces', 'traces'],
  ];
  const sourcesHtml = SOURCES.map(([label, key]) => {
    const s = bundle[key] as { status: string; data: unknown };
    const count = Array.isArray(s.data) ? s.data.length : s.data ? 1 : 0;
    return `<span class="src"><b style="color:${STATUS_COLOR[s.status] ?? '#888'}">●</b> ${esc(label)} <span class="dim">${esc(s.status)}${s.status === 'ok' ? ` · ${count}` : ''}</span></span>`;
  }).join('');

  const trxRows = trx
    .map(
      (t) => `<tr>
        <td><code>${esc(t.id)}</code></td>
        <td>${esc(t.action)}</td>
        <td class="num">${money(t.amount, t.currency)}</td>
        <td>${esc(t.currency)}</td>
        <td>${esc(t.rollbacked ? 'yes' : '')}</td>
        <td>${esc(formatTime(parseMysqlTime(t.created_at), 'utc', true))}</td>
        <td>${esc(formatIp(t.player_ip) ?? '')}</td>
      </tr>`,
    )
    .join('');

  const autoPrint = opts.autoPrint
    ? '<script>window.addEventListener("load",function(){setTimeout(function(){window.print()},250)})<\/script>'
    : '';

  return `<!doctype html><html lang="en"><head><meta charset="utf-8">
<title>Betlens — bet ${esc(r.bet_id)}</title>
<style>
  :root{color-scheme:light}
  *{box-sizing:border-box}
  body{font:13px/1.5 -apple-system,Segoe UI,Roboto,sans-serif;color:#14181d;background:#fff;margin:0;padding:28px 32px;max-width:900px}
  h1{font-size:16px;margin:0}
  h2{font-size:11px;text-transform:uppercase;letter-spacing:.08em;color:#5c6673;border-bottom:1px solid #e0ded8;padding-bottom:4px;margin:22px 0 10px}
  code{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:.92em}
  .dim{color:#8b95a3}
  .head{display:flex;justify-content:space-between;align-items:baseline;border-bottom:2px solid #14181d;padding-bottom:8px}
  .mark{font-weight:700;letter-spacing:.14em;text-transform:uppercase}
  table{width:100%;border-collapse:collapse}
  .meta th{text-align:left;color:#5c6673;font-weight:500;width:220px;padding:3px 0;vertical-align:top}
  .meta td{padding:3px 0}
  .data th{text-align:left;color:#5c6673;font-weight:600;font-size:10px;text-transform:uppercase;letter-spacing:.05em;border-bottom:1px solid #e0ded8;padding:4px 8px}
  .data td{border-bottom:1px solid #f1efeb;padding:3px 8px;font-family:ui-monospace,monospace;font-size:11px}
  .num{text-align:right}
  .hint{border-left:3px solid;padding:6px 10px;margin:6px 0;background:#faf9f7}
  .hint-h{margin-bottom:2px}
  .sev{text-transform:uppercase;font-size:10px;font-weight:700;letter-spacing:.05em}
  .src{display:inline-block;margin:0 12px 4px 0;white-space:nowrap;font-family:ui-monospace,monospace;font-size:11px}
  details{margin-top:8px}
  pre{background:#f7f6f3;border:1px solid #e0ded8;padding:10px;overflow:auto;font-size:10px;max-height:520px}
  @media print{body{padding:0}pre{max-height:none}}
</style></head><body>
  <div class="head"><h1><span class="mark">Betlens</span> · Bet diagnostic snapshot</h1><span class="dim">taken ${esc(takenAt)} UTC</span></div>

  <h2>Summary</h2>
  <table class="meta">${metaRows}</table>

  <h2>Hints</h2>
  ${hintsHtml}

  <h2>Sources</h2>
  <div>${sourcesHtml}</div>

  <h2>Transactions (${trx.length})</h2>
  <table class="data"><thead><tr><th>id</th><th>action</th><th class="num">amount</th><th>ccy</th><th>rolled back</th><th>created (UTC)</th><th>ip</th></tr></thead><tbody>${trxRows || '<tr><td colspan="7" class="dim">none</td></tr>'}</tbody></table>

  <h2>Full bundle (appendix)</h2>
  <details><summary class="dim">The complete raw response — everything above and every other source</summary>
    <pre>${esc(JSON.stringify(result, null, 2))}</pre>
  </details>
  ${autoPrint}
</body></html>`;
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function downloadHtml(result: BetlensResult): void {
  const html = buildReportHtml(result);
  triggerDownload(
    new Blob([html], { type: 'text/html' }),
    snapshotFilename(result).replace(/\.json$/, '.html'),
  );
}

/**
 * PDF via the browser's own print-to-PDF — no PDF library, so nothing bloats the
 * static bundle. Opens the report in a new top-level tab (outside the panel's
 * iframe sandbox) and auto-triggers print; the user picks "Save as PDF".
 */
export function openPdf(result: BetlensResult): boolean {
  const html = buildReportHtml(result, { autoPrint: true });
  const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
  const win = window.open(url, '_blank');
  if (!win) {
    // Popup blocked (or sandboxed): fall back to downloading the HTML so the
    // user can open and print it themselves.
    URL.revokeObjectURL(url);
    downloadHtml(result);
    return false;
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return true;
}
